import { LinkClickedEvent, TabClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { FluidContainer } from '@atlassian/mpac-primitives';
import { useEnvironment, useSource } from '@atlassian/mpac-utils';
import { AppsWithTextHighlights, HeadingWithTabs } from '@atlassian/mpac-widgets';
import { useState } from 'react';
import { useInView } from 'react-intersection-observer';

import {
  ANALYTICS_SUBJECT_ID_APPS_WITH_TEXT_HIGHLIGHTS_LINK,
  ANALYTICS_SUBJECT_ID_HEADING_WITH_TABS_TAB,
} from '../../../../../analytics/Events';
import useCTAClickHandler from '../../../../../hooks/useCTAClickHandler';
import { aiCollectionConfig } from '../../config/aiCollectionConfig.config';
import { CollectionsStructuredData } from '../StructuredData';
import { useCollectionsData } from './hooks/useCollectionsData';

import type { AppsWithTextHighlightsContent } from '@atlassian/mpac-widgets';

const allCategoryIds = aiCollectionConfig.categories.map((cat) => cat.id);

export const AIAppsCollections = () => {
  const source = useSource();
  const environment = useEnvironment();
  const [activeCategoryId, setActiveCategoryId] = useState<string>(
    aiCollectionConfig.categories[0].id
  );

  const { ref: containerRef, inView: isInView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const { apps, loading } = useCollectionsData({
    collectionId: aiCollectionConfig.id,
    categoryId: activeCategoryId,
    allCategoryIds,
    shouldPrefetchCategories: isInView,
  });
  const fireEvent = useAnalyticsEvents();
  const handleLinkClick = useCTAClickHandler();

  const handleTabChange = (tabId: string) => {
    setActiveCategoryId(tabId);
    fireEvent({
      eventType: TabClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HEADING_WITH_TABS_TAB,
      attributes: {
        tabId,
        sectionName: aiCollectionConfig.id,
      },
    });
  };

  const activeCategory = aiCollectionConfig.categories.find(
    (category) => category.id === activeCategoryId
  );

  return (
    <div ref={containerRef}>
      <CollectionsStructuredData apps={apps || []} />
      <FluidContainer>
        <HeadingWithTabs
          title={aiCollectionConfig.title}
          description={aiCollectionConfig.description}
          tabItems={aiCollectionConfig.categories}
          activeTabId={activeCategoryId}
          onTabChange={handleTabChange}
        />
      </FluidContainer>
      <AppsWithTextHighlights
        icon={activeCategory?.icon || ''}
        apps={apps || []}
        loading={loading}
        environment={environment}
        source={source}
        content={activeCategory as AppsWithTextHighlightsContent}
        clipPath={activeCategory?.clipPath}
        backgroundColor={activeCategory?.backgroundColor}
        textColor={activeCategory?.textColor}
        onLinkClick={(event) =>
          handleLinkClick(
            event,
            LinkClickedEvent,
            ANALYTICS_SUBJECT_ID_APPS_WITH_TEXT_HIGHLIGHTS_LINK,
            {
              destinationUrl: activeCategory?.linkHref,
              linkText: activeCategory?.linkText,
            },
            activeCategory?.linkHref
          )
        }
      />
    </div>
  );
};
